import schedulingConflictsComponentUrl from './schedulingConflictsComponent.html';

class SchedulingConflictsCtrl {
  constructor(
    Authentication,
    Utils,
    Restangular,
    PersonaCollection,
    uppercaseFirstFilter,
    dateFilter,
    toDateShortFilter,
    toTimeFilter
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      Utils,
      Restangular,
      PersonaCollection,
      uppercaseFirstFilter,
      dateFilter,
      toDateShortFilter,
      toTimeFilter,
    });
  }

  $onInit() {
    this.conflictsCount = 0;
    this.resolveConflicts = null;

    this.locationBasedConflicts = this.getLocationBasedConflicts();
    this.schedulingConflicts = this.getSchedulingConflicts();
    this.overtimeConflicts = this.getOvertimeConflicts();
    this.laborPercentageConflicts = this.getLaborPercentageConflicts();
    this.ptoConflicts = this.getPtoConflicts();
    this.restBreakConflicts = this.getRestBreakConflicts();
    this.consecutiveWorkDaysConflicts = this.getConsecutiveWorkDaysConflicts();

    this.conflictsText = this.conflictsCount === 1 ? 'conflict' : 'conflicts';

    const someEventsAreInThePast = this.conflictingShifts?.some(
      (s) => s.dtend < this.Utils.getNowDate()
    );

    this.resolveConflictsOptions = [
      { label: 'Leave assigned', value: null },
      { label: 'Make available', value: 'available', hidden: someEventsAreInThePast },
      { label: 'Make unassigned', value: 'unassign' },
      { label: 'Leave uncovered', value: 'delete' },
    ];
  }

  getLocationBasedConflicts() {
    let locationBasedConflicts = [];
    if (this.conflicts.location) {
      this.conflictsCount += this.conflicts.location.length;

      locationBasedConflicts = [
        ...locationBasedConflicts,
        ...this.conflicts.location.map((conflict) => {
          const location =
            this.PersonaCollection.personasDict[conflict.location.id] || conflict.location;
          return {
            location,
            hours: {
              amount: conflict.hours,
              limit: location.hoursCap,
            },
          };
        }),
      ];
    }

    if (this.conflicts.budget) {
      this.conflictsCount += this.conflicts.budget.length;

      locationBasedConflicts = [
        ...locationBasedConflicts,
        ...this.conflicts.budget.map((conflict) => {
          const location =
            this.PersonaCollection.personasDict[conflict.location.id] || conflict.location;
          return {
            location,
            budget: {
              amount: conflict.amount,
              limit: location.budget,
            },
          };
        }),
      ];
    }

    return locationBasedConflicts;
  }

  getSchedulingConflicts() {
    let schedulingConflicts = [];
    if (this.conflicts.conflicts) {
      const parsed = this.Utils.uniq(
        this.parseCalendarConflicts(this.conflicts.conflicts),
        (c) => `${c.user?.id}_${c.conflictText}`
      );
      schedulingConflicts = [...schedulingConflicts, ...parsed];
      this.conflictsCount += parsed.length;
    }

    if (this.conflicts.user) {
      schedulingConflicts = [
        ...schedulingConflicts,
        ...this.conflicts.user.map((conflict) => {
          const user = this.PersonaCollection.personasDict[conflict.user.id] || conflict.user;
          return {
            user,
            cap: {
              amount: conflict.hours,
              limit: user.hoursCap,
            },
          };
        }),
      ];
      this.conflictsCount += this.conflicts.user.length;
    }

    return schedulingConflicts;
  }

  getOvertimeConflicts() {
    let overtimeConflicts = [];
    if (this.conflicts.overtime) {
      this.conflicts.overtime.forEach(
        (c) => (c.user = this.PersonaCollection.personasDict[c.user.id])
      );
      this.overtimeHoursThreshold = this.Utils.roundNumber(
        this.Authentication.user.orgSettings.overtimeWeekThreshold / 60
      );
      this.conflictsCount += this.conflicts.overtime.length;

      overtimeConflicts = [...overtimeConflicts, ...this.conflicts.overtime];
    }

    return overtimeConflicts;
  }

  getLaborPercentageConflicts() {
    let laborPercentageConflicts = [];
    if (this.conflicts.laborPercent) {
      this.conflicts.laborPercent.forEach((conflict) => {
        conflict.date = this.Utils.getRealDate(conflict.date);
        conflict.realPercent = parseFloat(conflict.realPercent);
      });
      this.conflictsCount += this.conflicts.laborPercent.length;

      laborPercentageConflicts = [...laborPercentageConflicts, ...this.conflicts.laborPercent];
    }

    return laborPercentageConflicts;
  }

  getPtoConflicts() {
    let ptoConflicts = [];
    if (this.conflicts.ptoCapConflicts) {
      this.conflictsCount += this.conflicts.ptoCapConflicts.length;
      ptoConflicts = this.conflicts.ptoCapConflicts.map((conflict) => {
        const capUnit =
          this.Authentication.user.orgSettings.leaveCapUnit === 'HOURS'
            ? 60
            : this.Authentication.user.orgSettings.workDayMinutes;
        const user = this.PersonaCollection.personasDict[conflict.user.id] || conflict.user;
        return {
          ...conflict,
          user,
          exceeded: (conflict.existing + conflict.requested) / capUnit,
          available: conflict.cap / capUnit,
        };
      });
    }

    return ptoConflicts;
  }

  getRestBreakConflicts() {
    if (this.conflicts.restBreakConflicts) {
      const minRestHours = this.Authentication.user.orgSettings.restBreakThreshold / 60;
      this.conflictsCount += this.conflicts.restBreakConflicts.length;

      return this.conflicts.restBreakConflicts.map((conflict) => ({
        minRestHours,
        restTimeMinutes: conflict.restTimeMinutes,
        day: this.dateFilter(conflict.existingShift?.dtstart, 'MMMM dd'),
        user:
          this.PersonaCollection.personasDict[conflict.existingShift?.user.id] ||
          conflict.existingShift?.user,
      }));
    }

    return [];
  }

  getConsecutiveWorkDaysConflicts() {
    if (this.conflicts.consecutiveWorkDaysConflicts) {
      this.conflictsCount += this.conflicts.consecutiveWorkDaysConflicts.length;

      return this.conflicts.consecutiveWorkDaysConflicts.map((conflict) => ({
        user: this.PersonaCollection.personasDict[conflict.userId],
      }));
    }

    return [];
  }

  _getConflictTextSuffix(conflict) {
    const day = this.dateFilter(conflict.dtstart, 'MMMM dd');
    const dateStart = this.toDateShortFilter(conflict.dtstart);
    const dateEnd = this.toDateShortFilter(conflict.dtend);
    const timeStart = this.toTimeFilter(conflict.dtstart);
    const timeEnd = this.toTimeFilter(conflict.dtend);

    if (conflict.fullDay) {
      if (dateStart === dateEnd) {
        return ` • ${day}`;
      }

      return ` • ${dateStart} to ${dateEnd}`;
    }

    if (dateStart === dateEnd) {
      return ` • ${day} • ${timeStart} to ${timeEnd}`;
    }

    return ` • ${dateStart} ${timeStart} to ${dateEnd} ${timeEnd}`;
  }

  parseCalendarConflicts(conflicts) {
    return conflicts.map((conflict) => {
      let user;

      if (conflict.user) {
        user = this.Restangular.restangularizeElement(null, conflict.user, 'users');
      }

      if (conflict.location) {
        conflict.location =
          this.PersonaCollection.personasDict[conflict.location.id] || conflict.location;
      }

      if (conflict.position) {
        conflict.position =
          this.PersonaCollection.personasDict[conflict.position.id] || conflict.position;
      }

      const textByType = {
        leave: 'Time off',
        leave_vacation: 'Time off',
        leave_vacation_unpaid: 'Time off',
        leave_sick: 'Time off',
        leave_holiday: 'Time off',
        leave_personal: 'Time off',
        leave_other: 'Time off',
        availability: user ? 'Unavailable' : 'Unavailability',
        suppression: 'Time off requests are not allowed',
        business_closed: 'Business closed',
      };

      let conflictText =
        conflict.type in textByType
          ? textByType[conflict.type]
          : `${user ? 'Already scheduled' : 'Shift'} at ${conflict.location.name}`;

      conflict.dtstart = this.Utils.getRealDate(conflict.dtstart);
      conflict.dtend = this.Utils.getRealDate(conflict.dtend);

      conflictText += this._getConflictTextSuffix(conflict);

      return {
        user,
        conflictText,
      };
    });
  }
}

export default {
  bindings: {
    conflicts: '<',
    cancel: '<',
    action: '<',
    abort: '&',
    continue: '&',
    onlyManagerCanIgnore: '<',
    canResolveConflicts: '<',
    conflictingShifts: '<',
  },
  controller: SchedulingConflictsCtrl,
  controllerAs: 'ctrl',
  templateUrl: schedulingConflictsComponentUrl,
};
